import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Settings, User, LogOut, ChevronDown, BarChart3, Calculator, Network, Wrench, BotMessageSquare, Presentation, MessageSquare, ArrowRight, UserPlus, Building2, Mail, Phone, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { createLeadInternal } from "@/lib/api";
import { toast } from "sonner";

const LOGO_URL = "/absolin-logo-transparent.png";

const TOOLS_MENU_BASE = [
  { href: "/assessment", label: "Digital Maturity Assessment", icon: BarChart3, desc: "Score your digital readiness" },
  { href: "/codn-calculator", label: "CODN Calculator", icon: Calculator, desc: "Calculate your hidden costs" },
  { href: "/system-mapper", label: "System Landscape Mapper", icon: Network, desc: "Map your IT ecosystem" },
];

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", company: "", company_size: "", message: "", vertical_interest: "" });
  const [savingLead, setSavingLead] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const toolsRef = useRef(null);

  const isAuthenticated = !!userData?.email;
  const isInternal = userData?.user_type !== 'external';
  const TOOLS_MENU = TOOLS_MENU_BASE;

  // Fetch fresh user data from backend so permission changes don't need re-login
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    api.get('/auth/me').then(res => {
      const d = res.data;
      const user = { email: d.email, name: d.name, role: d.role, assigned_verticals: d.assigned_verticals, user_type: d.user_type, can_access_collections: d.can_access_collections, can_access_presales: d.can_access_presales };
      localStorage.setItem('user_data', JSON.stringify(user));
      setUserData(user);
    }).catch(() => {
      // Fallback to localStorage if API fails
      const userDataStr = localStorage.getItem('user_data');
      if (userDataStr) { try { setUserData(JSON.parse(userDataStr)); } catch {} }
    });
  }, []);

  // Shrink header on scroll for modern collapse-on-scroll feel
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Check for pending feedback (site-wide)
  useEffect(() => {
    const checkPending = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const res = await api.get('/feedback/pending');
          setPendingFeedback(res.data?.pending || []);
        }
      } catch {}
    };
    checkPending();
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (toolsRef.current && !toolsRef.current.contains(event.target)) {
        setToolsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email) { toast.error("Name and email are required"); return; }
    setSavingLead(true);
    try {
      await createLeadInternal(leadForm);
      toast.success("Lead created successfully");
      setShowCreateLead(false);
      setLeadForm({ name: "", email: "", phone: "", company: "", company_size: "", message: "", vertical_interest: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create lead");
    }
    setSavingLead(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    sessionStorage.removeItem('audit_session_id');
    navigate('/login');
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/verticals", label: "Industries" },
    { href: "/resources", label: "Resources" },
    { href: "/our-customers", label: "Our Happy Customers" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_-8px_rgba(15,23,42,0.08)] border-b border-slate-200/60"
          : "bg-white/70 backdrop-blur-md border-b border-slate-100/60"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-20"}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group transition-transform duration-200 hover:scale-[1.02]" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="Absolin"
              className={`w-auto object-contain transition-all duration-300 ${scrolled ? "h-8 md:h-9" : "h-9 md:h-11"}`}
              draggable="false"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Pill-style nav links with animated underline */}
            <div className="flex items-center gap-1 px-1">
              {navLinks.map((link) => {
                const active = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`relative text-sm font-medium px-3.5 py-2 rounded-full transition-colors ${
                      active
                        ? "text-[#0066CC] bg-blue-50/80"
                        : "text-slate-600 hover:text-[#0066CC] hover:bg-slate-50"
                    }`}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Pre-Sales Hub (Internal Users) */}
            {isAuthenticated && (userData?.role === 'admin' || !!userData?.can_access_presales) && (
              <Link to="/pre-sales" className={`text-sm font-semibold ml-2 px-3.5 py-2 rounded-full transition-colors ${
                location.pathname === "/pre-sales" ? "bg-[#0066CC] text-white shadow-sm shadow-blue-200" : "text-[#0066CC] bg-blue-50/60 hover:bg-blue-100"
              }`} data-testid="nav-presales-link">
                Pre-Sales Hub
              </Link>
            )}

            {/* Collection Management */}
            {isAuthenticated && (userData?.role === 'admin' || !!userData?.can_access_collections) && (
              <Link to="/collections" className={`text-sm font-semibold px-3.5 py-2 rounded-full transition-colors ${
                location.pathname === "/collections" ? "bg-emerald-600 text-white shadow-sm shadow-emerald-200" : "text-emerald-700 bg-emerald-50/60 hover:bg-emerald-100"
              }`} data-testid="nav-collections-link">
                Collections
              </Link>
            )}

            {/* Vertical separator before action cluster */}
            <div className="h-6 w-px bg-slate-200 mx-2" />

            <div className="flex items-center gap-2">
              {/* Tools Dropdown Icon */}
              <div className="relative" ref={toolsRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full w-10 h-10 hover:bg-indigo-50 hover:text-indigo-600 transition-all"
                  onMouseEnter={() => setToolsDropdownOpen(true)}
                  onClick={() => setToolsDropdownOpen(!toolsDropdownOpen)}
                  data-testid="nav-tools-btn"
                  title="Business Tools"
                >
                  <Wrench className="w-[18px] h-[18px] text-slate-600" />
                </Button>
                {toolsDropdownOpen && (
                  <div
                    className="absolute right-0 mt-3 w-72 bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 py-2 z-50 animate-fade-in-up"
                    onMouseEnter={() => setToolsDropdownOpen(true)}
                    onMouseLeave={() => setToolsDropdownOpen(false)}
                  >
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em]">Self-Discovery Tools</p>
                    </div>
                    {TOOLS_MENU.map((tool) => (
                      <Link
                        key={tool.href}
                        to={tool.href}
                        className={`flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50 transition-colors ${
                          location.pathname === tool.href ? "bg-blue-50 text-[#0066CC]" : "text-slate-700"
                        }`}
                        onClick={() => setToolsDropdownOpen(false)}
                        data-testid={`nav-tool-${tool.href.slice(1)}`}
                      >
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center shrink-0 border border-indigo-100/60">
                          <tool.icon className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-sm leading-tight">{tool.label}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{tool.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Create Lead (Internal Users - authenticated only) */}
              {isAuthenticated && (userData?.role === 'admin' || userData?.user_type !== 'external') && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full w-10 h-10 hover:bg-amber-50 hover:text-amber-600 transition-all"
                  onClick={() => setShowCreateLead(true)}
                  title="Create Lead"
                  data-testid="nav-create-lead-btn"
                >
                  <UserPlus className="w-[18px] h-[18px] text-slate-600" />
                </Button>
              )}

              {/* User Profile or Login Button */}
              {isAuthenticated ? (
              <div className="relative ml-1" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-slate-200 hover:border-[#0066CC]/40 hover:bg-blue-50/40 transition-all"
                  data-testid="user-profile-btn"
                >
                  <div className="w-7 h-7 bg-gradient-to-br from-[#0066CC] to-[#0052A3] rounded-full flex items-center justify-center shadow-sm">
                    <User className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 max-w-[100px] truncate">
                    {userData?.email?.split('@')[0] || 'User'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl shadow-slate-300/40 border border-slate-100 py-2 z-50 animate-fade-in-up">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-semibold text-slate-900">{userData?.email || 'User'}</p>
                      <p className="text-xs text-slate-500 capitalize mt-0.5">{userData?.role || 'user'} Account</p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <User className="w-4 h-4" /> My Profile
                      </Link>
                      {userData?.role === 'admin' && (
                        <Link
                          to="/admin-portal"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                          onClick={() => setProfileDropdownOpen(false)}
                          data-testid="nav-admin-btn"
                        >
                          <Settings className="w-4 h-4" /> Admin Panel
                        </Link>
                      )}
                    </div>
                    <div className="border-t border-slate-100 pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" /> Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
              ) : (
                <Link to="/login" data-testid="nav-login-btn" className="group relative inline-flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border border-slate-200 bg-white hover:border-[#0066CC]/40 hover:bg-blue-50/40 hover:shadow-sm transition-all">
                  {/* Avatar-style micro circle with gradient — communicates "sign-in" without shouting */}
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#0066CC] to-[#0052A3] text-white shadow-inner">
                    <User className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-sm font-semibold text-slate-700 group-hover:text-[#0066CC] transition-colors">Sign in</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#0066CC] group-hover:translate-x-0.5 transition-all" />
                </Link>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-full text-slate-600 hover:text-[#0066CC] hover:bg-slate-50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-3 border-t border-slate-100 animate-fade-in-up max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link key={link.href} to={link.href}
                  className={`text-sm font-medium py-2.5 px-2 rounded-lg ${location.pathname === link.href ? "text-[#0066CC] bg-blue-50" : "text-slate-600 active:bg-slate-100"}`}
                  onClick={() => setIsMenuOpen(false)}
                >{link.label}</Link>
              ))}

              {/* Pre-Sales Hub (Internal Users) */}
              {isAuthenticated && (userData?.role === 'admin' || !!userData?.can_access_presales) && (
                <Link to="/pre-sales"
                  className={`text-sm font-semibold py-2.5 px-2 rounded-lg ${location.pathname === "/pre-sales" ? "bg-[#0066CC] text-white" : "text-[#0066CC] bg-blue-50 active:bg-blue-100"}`}
                  onClick={() => setIsMenuOpen(false)}>
                  Pre-Sales Hub
                </Link>
              )}

              {/* Collection Management */}
              {isAuthenticated && (userData?.role === 'admin' || !!userData?.can_access_collections) && (
                <Link to="/collections"
                  className={`text-sm font-semibold py-2.5 px-2 rounded-lg ${location.pathname === "/collections" ? "bg-emerald-600 text-white" : "text-emerald-700 bg-emerald-50 active:bg-emerald-100"}`}
                  onClick={() => setIsMenuOpen(false)}>
                  Collections
                </Link>
              )}

              <div className="border-t border-slate-100 pt-2 mt-1">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 px-2">Tools</p>
                {TOOLS_MENU.map((tool) => (
                  <Link key={tool.href} to={tool.href}
                    className="flex items-center gap-2.5 text-sm font-medium py-2.5 px-2 text-slate-600 rounded-lg active:bg-slate-100"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <tool.icon className="w-4 h-4 text-[#0066CC]" /> {tool.label}
                  </Link>
                ))}
              </div>
              {isAuthenticated ? (
              <>
              <div className="border-t border-slate-100 pt-2 mt-1">
                {userData?.role === 'admin' && (
                  <Link to="/admin-portal" className="flex items-center gap-2.5 text-sm font-medium py-2.5 px-2 text-slate-600 rounded-lg active:bg-slate-100" onClick={() => setIsMenuOpen(false)}>
                    <Settings className="w-5 h-5" /> Admin Panel
                  </Link>
                )}
              </div>
              <div className="border-t border-slate-100 pt-2 mt-1">
                <button onClick={() => { setIsMenuOpen(false); handleLogout(); }} className="flex items-center gap-2.5 text-sm font-medium py-2.5 px-2 text-red-600 rounded-lg w-full active:bg-red-50">
                  <LogOut className="w-5 h-5" /> Logout
                </button>
              </div>
              </>
              ) : (
              <div className="border-t border-slate-100 pt-2 mt-1">
                <Link to="/login" className="flex items-center gap-2.5 text-sm font-semibold py-2.5 px-2 text-[#0066CC] rounded-lg active:bg-blue-50" onClick={() => setIsMenuOpen(false)}>
                  <User className="w-5 h-5" /> Login
                </Link>
              </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pending Feedback Banner (site-wide) */}
      {pendingFeedback.length > 0 && (
        <div className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg" data-testid="pending-feedback-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <MessageSquare className="w-4 h-4 shrink-0" />
              <p className="text-sm font-medium truncate">
                Pending feedback: <strong>{pendingFeedback[0].title}</strong>
              </p>
            </div>
            <Link to={`/feedback/${pendingFeedback[0].token}`}>
              <Button size="sm" className="bg-white text-blue-700 hover:bg-blue-50 font-semibold gap-1 shrink-0 text-xs" data-testid="submit-feedback-cta">
                Submit Feedback <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Create Lead Dialog (Internal Users) */}
      <Dialog open={showCreateLead} onOpenChange={setShowCreateLead}>
        <DialogContent className="max-w-lg" data-testid="create-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-600" />
              Create New Lead
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    placeholder="Prospect name" className="pl-9 h-9 text-sm" required data-testid="lead-form-name" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    placeholder="email@company.com" className="pl-9 h-9 text-sm" required data-testid="lead-form-email" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                    placeholder="+91 98765 43210" className="pl-9 h-9 text-sm" data-testid="lead-form-phone" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Company</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input value={leadForm.company} onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                    placeholder="Company name" className="pl-9 h-9 text-sm" data-testid="lead-form-company" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Industry / Vertical Interest</Label>
              <Input value={leadForm.vertical_interest} onChange={(e) => setLeadForm({ ...leadForm, vertical_interest: e.target.value })}
                placeholder="e.g., Manufacturing, Pharma, Retail" className="h-9 text-sm" data-testid="lead-form-vertical" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes / Message</Label>
              <Textarea value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                placeholder="Any additional notes about this prospect..." rows={2} className="text-sm" data-testid="lead-form-message" />
            </div>
            <Button type="submit" disabled={savingLead} className="w-full bg-amber-600 hover:bg-amber-700 gap-2" data-testid="lead-form-submit">
              {savingLead ? "Creating..." : "Create Lead"}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </nav>
  );
};

